/**
 * <cba-quick-add-host> — the quick-add picker (snippets/cba-quick-add-drawer;
 * plan 6.1h: was the snippet's inline script). Mounted once per page, from
 * the footer.
 *
 *   <cba-quick-add-host data-add-label="Add to cart" data-sold-out="Sold out"
 *     data-unavailable="Unavailable">
 *     <cba-dialog name="quick-add" stack>
 *       <dialog data-quick-add-sheet>… [data-quick-add-sheet-title]
 *         [data-quick-add-sheet-body] …</dialog>
 *     </cba-dialog>
 *   </cba-quick-add-host>
 *
 * - Opens when a shopper submits a product card's Add form
 *   (.cba-product-card__add-form) or the product page's sticky Add
 *   ([data-cba-quick-add-form]). The trigger's [data-product-handle] context
 *   names the product, and its [data-product-url] (the product's .url, so the
 *   market prefix is kept) is where the panel comes from: {url}?view=quick-add,
 *   the .cba-quick-add element of templates/product.quick-add. Panels are
 *   cached per handle, so a re-open is instant.
 * - Below 1024 px, and for a trigger without a card: a bottom sheet, the
 *   shared <cba-dialog> (the page behind is inert, Escape closes, focus
 *   returns to the Add control). From 1024 px: a panel that rises inside the
 *   clicked card; Escape or a click outside closes it. A card with no room
 *   for the product's options and the Add button (two side by side in the
 *   cart drawer; many sizes in a narrow column) gets the sheet instead, so
 *   the button is never below a fold inside the card.
 * - Crossing the breakpoint while open re-opens in the other presentation.
 * - Add to cart is @theme/cart's (an ES module; DESIGN-PLAN 7.G2): the
 *   button is busy until the cart answers, the picker closes only when the
 *   line is in, and a refused or failed add says why under the button (site
 *   audit B10.1). The cart drawer, the header count and the added-to-cart
 *   popup follow Shopify's shopify:cart:lines-update.
 * - One panel at a time: a second tap while a panel is loading is ignored
 *   (B10.2), and a panel that cannot load opens the product page (B10.3).
 * - Every document listener goes when the element leaves the page.
 */
import { addToCart, CartDeclinedError } from '@theme/cart';

if (!customElements.get('cba-quick-add-host')) {
  const DESKTOP_MQ = '(min-width: 1024px)';
  const ANIM_MS = 350;
  const TRIGGER = '.cba-product-card__add-form, [data-cba-quick-add-form]';
  const cache = {}; /* handle -> .cba-quick-add outerHTML */

  class CbaQuickAddHost extends HTMLElement {
    connectedCallback() {
      this.sheet = this.querySelector('[data-quick-add-sheet]');
      if (!this.sheet) return;
      /* Opening, closing and the scroll lock are the shared <cba-dialog>'s. */
      this.dialog = this.sheet.closest('cba-dialog');
      this.sheetBody = this.sheet.querySelector('[data-quick-add-sheet-body]');
      this.sheetTitle = this.sheet.querySelector('[data-quick-add-sheet-title]');
      this.state = null; /* { mode, panel, card, host, handle, url } */
      this.abort = new AbortController();
      const { signal } = this.abort;

      /* ---- triggers: the card Add forms and the PDP sticky Add ---- */
      document.addEventListener('submit', (e) => {
        const form = e.target.closest(TRIGGER);
        if (!form) return;
        const context = form.closest('[data-product-handle]');
        const handle = context && context.getAttribute('data-product-handle');
        /* Cards saved by recently viewed before 6.1h carry no
           data-product-url; their name links to the same .url. */
        const link = context && context.querySelector('a.cba-product-card__name');
        const url = context && (context.getAttribute('data-product-url') || (link && link.getAttribute('href')));
        if (!handle || !url) return; /* the form posts on its own */
        e.preventDefault();
        /* Prefer the .cba-product-card (the desktop in-card panel needs it);
           fall back to the [data-product-handle] context. The PDP sticky has no
           card, so it passes its wrapper, which carries the synced
           [data-cba-variant-id] (the picker pre-selects the buy-box choice),
           and opens as the sheet. */
        this.openQuickAdd(handle, url, form.closest('.cba-product-card') || context);
      }, { signal });

      /* ---- close affordances ---- */
      /* The X, Escape and the backdrop close the sheet through <cba-dialog>;
         however it closed, the panel goes with it. */
      this.dialog.addEventListener('cba-dialog:close', () => {
        if (this.state && this.state.mode === 'sheet') this.state = null;
        this.sheetBody.innerHTML = '';
        this.sheetTitle.textContent = '';
      }, { signal });
      /* While the sheet is open it owns Escape. The overlays it can open over
         (cart drawer, search) still listen on document, and closing them too
         would leave focus nowhere to return. */
      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.sheet.open) e.stopPropagation();
      }, { capture: true, signal });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.state && this.state.mode === 'incard') this.close();
      }, { signal });
      /* The in-card panel has no backdrop: a click outside it closes it. */
      document.addEventListener('click', (e) => {
        if (!this.state || this.state.mode !== 'incard') return;
        if (e.target.closest('.cba-product-card__add-form')) return;
        if (!this.state.host.contains(e.target)) this.close();
      }, { signal });

      /* ---- viewport change ---- A panel opened in one presentation must not
         linger in the other. If the breakpoint is crossed while open, the
         current one goes at once (no exit animation, so the in-card panel
         cannot flash on mobile or the sheet on desktop) and the picker
         re-opens in the right one. */
      const mq = window.matchMedia(DESKTOP_MQ);
      if (mq.addEventListener) mq.addEventListener('change', () => this.onBreakpointChange(), { signal });
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      if (this.state && this.state.mode === 'incard') this.removeIncard(this.state);
      this.state = null;
    }

    isDesktop() { return window.matchMedia(DESKTOP_MQ).matches; }

    /* ---- fetch and cache the product's panel ---- */
    fetchPanel(handle, url) {
      if (cache[handle]) return Promise.resolve(cache[handle]);
      const src = new URL(url, window.location.origin);
      src.searchParams.set('view', 'quick-add');
      return fetch(src, { headers: { 'X-Requested-With': 'fetch' } })
        .then((r) => { if (!r.ok) throw new Error('quick-add fetch ' + r.status); return r.text(); })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const panel = doc.querySelector('.cba-quick-add');
          if (!panel) throw new Error('quick-add panel missing for ' + handle);
          cache[handle] = panel.outerHTML;
          return cache[handle];
        });
    }

    /* ---- wire one injected panel ---- */
    wirePanel(panel, card) {
      let variants = [];
      const dataEl = panel.querySelector('[data-quick-add-variants]');
      if (dataEl) { try { variants = JSON.parse(dataEl.textContent); } catch (e) { /* no variants: Add stays off */ } }

      const atcBtn = panel.querySelector('[data-quick-add-atc]');
      const atcText = panel.querySelector('[data-quick-add-atc-text]');
      const inputs = panel.querySelectorAll('[data-quick-add-input]');
      /* A ring with sizes: none is checked until the shopper picks one; Add
         without a size says "Choose a size" instead of adding (Leo,
         2026-10-09). */
      const sizeInputs = panel.querySelectorAll('[data-quick-add-size]');
      const sizeError = panel.querySelector('[data-quick-add-size-error]');
      const sizeMissing = () => sizeInputs.length > 0 && !panel.querySelector('[data-quick-add-size]:checked');
      const clearSizeError = () => { if (sizeError) { sizeError.hidden = true; sizeError.textContent = ''; } };

      const resolveVariant = () => {
        if (inputs.length === 0) return variants[0] || null;
        const sel = Array.from(panel.querySelectorAll('[data-quick-add-input]:checked'), (i) => i.value);
        return variants.find((v) => sel.length === v.options.length && sel.every((s) => v.options.indexOf(s) !== -1)) || null;
      };
      const refresh = () => {
        const v = resolveVariant();
        panel.__cbaVariant = v;
        if (atcBtn) {
          /* No size yet is not "unavailable": Add stays on and asks for one. */
          const waiting = !v && sizeMissing();
          atcBtn.disabled = !waiting && (!v || !v.available);
          if (atcText) atcText.textContent = waiting ? this.dataset.addLabel : !v ? this.dataset.unavailable : !v.available ? this.dataset.soldOut : this.dataset.addLabel;
        }
        panel.querySelectorAll('[data-quick-add-input]:checked').forEach((i) => {
          const span = panel.querySelector(`[data-quick-add-selected-for="${CSS.escape(i.name)}"]`);
          if (span) span.textContent = i.value;
        });
      };

      /* Pre-select the card's current variant (a colour the shopper already
         picked on the card's dots, or the PDP buy box). Its size only when
         the shopper chose it on the product page ([data-cba-size-chosen] on
         the sticky bar); a card's variant is just its first one. */
      const cardIdEl = card && card.querySelector('[data-cba-variant-id]');
      const current = cardIdEl && cardIdEl.value && variants.find((v) => String(v.id) === String(cardIdEl.value));
      const sizeChosen = !!(card && card.hasAttribute('data-cba-size-chosen'));
      if (current) {
        current.options.forEach((optVal) => {
          inputs.forEach((i) => {
            if (i.value === optVal && (sizeChosen || !i.hasAttribute('data-quick-add-size'))) i.checked = true;
          });
        });
      }
      panel.addEventListener('change', (e) => {
        if (e.target && e.target.matches('[data-quick-add-size]')) clearSizeError();
        if (e.target && e.target.matches('[data-quick-add-input]')) refresh();
      });
      const addError = panel.querySelector('[data-quick-add-error]');
      const showAddError = (text) => { if (addError) { addError.textContent = text; addError.hidden = !text; } };
      if (atcBtn) {
        atcBtn.addEventListener('click', () => {
          if (atcBtn.getAttribute('aria-busy') === 'true') return;
          if (sizeMissing()) {
            if (sizeError) { sizeError.hidden = false; sizeError.textContent = sizeError.getAttribute('data-message'); }
            sizeInputs[0].focus();
            return;
          }
          const v = panel.__cbaVariant || resolveVariant();
          if (!v || !v.available) return;
          showAddError('');
          atcBtn.setAttribute('aria-busy', 'true');
          addToCart([{ id: v.id, quantity: 1 }], { element: atcBtn, context: 'dialog' })
            .then(() => this.close())
            .catch((error) => {
              showAddError(error instanceof CartDeclinedError && error.message ? error.message : this.dataset.error);
            })
            .finally(() => atcBtn.removeAttribute('aria-busy'));
        });
      }
      panel.querySelectorAll('[data-quick-add-close]').forEach((b) => b.addEventListener('click', () => this.close()));

      refresh();
    }

    /* ---- open and close ---- */
    openSheet(html, card, handle, url) {
      this.sheetBody.innerHTML = html;
      const panel = this.sheetBody.querySelector('.cba-quick-add');
      const title = panel.querySelector('[data-quick-add-title]');
      this.sheetTitle.textContent = title ? title.textContent : '';
      this.wirePanel(panel, card);
      this.state = { mode: 'sheet', panel, card, handle, url };
      this.dialog.open();
    }

    /* False when the card has no room for the panel: nothing is left in the
       card, and the caller opens the sheet. Measured while the panel is still
       below the card's edge, so nothing shows. */
    openIncard(html, card, handle, url) {
      const host = document.createElement('div');
      host.className = 'cba-quick-add-incard';
      host.setAttribute('data-quick-add-incard', '');
      host.innerHTML = html;
      card.style.overflow = 'hidden';
      card.appendChild(host);
      if (host.scrollHeight > host.clientHeight + 1) {
        host.remove();
        card.style.overflow = '';
        return false;
      }
      const panel = host.querySelector('.cba-quick-add');
      this.wirePanel(panel, card);
      this.state = { mode: 'incard', panel, card, host, handle, url };
      requestAnimationFrame(() => host.classList.add('cba-quick-add-incard--open'));
      return true;
    }

    removeIncard(st) {
      st.host.remove();
      st.card.style.overflow = '';
    }

    close() {
      if (!this.state) return;
      const st = this.state;
      this.state = null;
      if (st.mode === 'sheet') {
        /* Slides out, then closes: focus returns to the Add control. */
        this.dialog.close();
      } else {
        st.host.classList.remove('cba-quick-add-incard--open');
        setTimeout(() => this.removeIncard(st), ANIM_MS);
      }
    }

    openQuickAdd(handle, url, cardEl) {
      if (this.loading) return;
      if (this.state) this.close();
      this.loading = true;
      this.fetchPanel(handle, url).then((html) => {
        const incard = this.isDesktop() && cardEl && cardEl.classList.contains('cba-product-card');
        if (!incard || !this.openIncard(html, cardEl, handle, url)) this.openSheet(html, cardEl || null, handle, url);
      }).catch(() => {
        /* No panel: the product page has the same choice. */
        window.location.href = url;
      }).finally(() => { this.loading = false; });
    }

    onBreakpointChange() {
      const st = this.state;
      if (!st) return;
      const shouldBeIncard = this.isDesktop() && st.card && st.card.classList.contains('cba-product-card');
      if (shouldBeIncard === (st.mode === 'incard')) return; /* already right */
      this.state = null;
      if (st.mode === 'incard') this.removeIncard(st);
      else this.dialog.closeNow();
      this.openQuickAdd(st.handle, st.url, st.card);
    }
  }

  customElements.define('cba-quick-add-host', CbaQuickAddHost);
}
